/**
 * @file Capitan
 * @desc Global Object for Capitan
 * @module Capitan/Capitan
 * @copyright Brandung GmbH & Co KG (http://www.brandung.de/)
 * @author Sebastian Steiner
 * @version 2.0
 * @license MIT License (MIT)
 * @date 21.01.2018
 *
 */
import CapGetComputedStyle from './es6/CapGetComputedStyle';
import CapSmartResize from './es6/CapSmartResize';
import UAParser from 'ua-parser-js';

class Capitan {
  constructor() {
    this.Vars = {};
    this.initObject();
    this.setEventClassListener();
    this.initResizer();
    this.resizeHandler();
    this.initBreakpoints();
    this.initDeviceCheck();
    this.initClasses();
    this.initBrowserCheck();
  }

  initResizer() {
    CapSmartResize(this.resizeHandler, 100, false, this);
  }

  getBreakpoints() {
    return this.Vars.breakpoints;
  }

  assertBreakpoint(operator, breakpoint) {
    const breakpoints = Object.keys(this.getBreakpoints()),
          curBreakpointIndex = breakpoints.indexOf(this.getCurrentBreakpoint()),
          conditionalBreakpointIndex = breakpoints.indexOf(breakpoint);

    switch (operator) {
      case 'eq':
        return curBreakpointIndex === conditionalBreakpointIndex;

      case 'lt':
        return curBreakpointIndex < conditionalBreakpointIndex;

      case 'ht':
        return curBreakpointIndex > conditionalBreakpointIndex;

      default:
        return false;
    }
  }

  getCurrentBreakpoint() {
    const windowWidth = window.innerWidth;
    const breakpoints = Object.keys(this.Vars.breakpoints);
    let breakpoint = '';

    for (let i = breakpoints.length - 1; i >= 0; i -= 1) {
      breakpoint = this.Vars.breakpoints[breakpoints[i]];

      if (windowWidth >= breakpoint) {
        return breakpoints[i];
      }

      if (i === 0 && windowWidth < breakpoint) {
        return breakpoints[i];
      }
    }
  }

  getCurrentOrientation() {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const htmlNode = this.Vars.html;

    if (windowWidth <= windowHeight) {
      return 'portrait';
    }

    return 'landscape';
  }

  CapEventHandling(el, event, content) {
    var event = document.createEvent('CustomEvent');
    event.initCustomEvent(event, true, true, content);
    el.dispatchEvent(event);
  }

  resizeHandler() {
    const currentBreakpoint = this.getCurrentBreakpoint();
    const currentOrientation = this.getCurrentOrientation();
    const breakpoint = `on-breakpoint-${currentBreakpoint}`;
    const orientation = `on-orientation-${currentOrientation}`;
    this.Vars.currentBreakpoint = currentBreakpoint;
    this.Vars.currentOrientation = currentOrientation;
    this.Vars.body.className = this.Vars.body.className.replace(/\s?on-breakpoint-(xs|sm|md|lg|xl|xxl|hd|mhd|uhd)/g, '');
    this.Vars.body.classList.add(`on-breakpoint-${this.Vars.currentBreakpoint}`);
    this.Vars.body.className = this.Vars.body.className.replace(/\s?on-orientation-(landscape|portrait)/g, '');
    this.Vars.body.classList.add(`on-orientation-${this.Vars.currentOrientation}`);
  }

  initBreakpoints() {
    try {
      this.Vars.breakpoints = JSON.parse(CapGetComputedStyle('body', ':before', 'content'));
    } catch (e) {}
  }

  initDeviceCheck() {
    var parser = new UAParser();
    let result = parser.getResult().device.type;

    if (typeof result != 'undefined') {
      this.Vars.currentDevice = result;
    }
  }

  initBrowserCheck() {
    let parser = new UAParser();
    const browserCheck = document.querySelector('.browser-check--get-browser');
    let browserDetection = parser.getResult().browser.name + '&nbsp;' + 'Version' + '&nbsp;' + parser.getResult().browser.version.slice(0, 6);
    browserDetection = '&nbsp;' + browserDetection + '&nbsp;';
    const browserDetectionElement = document.querySelector('.browser-check--get-browser');

    if (typeof browserDetection != 'undefined' && browserCheck) {
      browserCheck.innerHTML = browserDetection;
    }
  }

  initObject() {
    this.Vars = {
      // path to assets folder
      folderPath: 'Config.paths.liveUrl',
      // standard breakpoints
      breakpoints: {
        xs: 320,
        sm: 480,
        md: 768,
        lg: 991,
        xl: 1280,
        xxl: 1440,
        hd: 1920,
        mhd: 2440,
        uhd: 3840
      },
      // mobile first (xs, sm, md, lg, xl)
      currentBreakpoint: 'xs',
      // portrait first (portrait, landscape)
      currentOrientation: 'portrait',
      currentDevice: '',
      isNativeMobile: window.navigator.userAgent.indexOf('Android') > 0 || // Android
      window.navigator.userAgent.indexOf('iPhone') > 0 || // iPhone
      window.navigator.userAgent.indexOf('iPad') > 0,
      // iPad
      isFF: window.navigator.userAgent.indexOf('Firefox') > -1,
      // Firefox
      isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream,
      html: document.querySelector('html'),
      body: document.querySelector('body'),
      window: document.window,
      doc: document
    }; // CSS components script namespace

    this.Component = {}; // functions must return something

    this.Function = {}; // everything that has to do with event handling

    this.Handle = {}; // Should be used for scripts that have nothing to do with components, e.g. placeholder polyfills, plugins, etc.

    this.Util = {};
  }

  setEventClassListener() {
    this.Vars.doc.addEventListener('on-set-class', (event, className) => {
      this.Vars.body.classList.add(event.detail);
    });
  }

  initClasses() {
    this.Vars.body.classList.add(`on-breakpoint-${this.Vars.currentBreakpoint}`);
    this.Vars.body.classList.add(`on-orientation-${this.Vars.currentOrientation}`);

    if (this.Vars.isFF) {
      this.Vars.body.classList.add(`is-FF`);
    }

    if (this.Vars.isIOS) {
      this.Vars.body.classList.add(`is-IOS`);
    }

    if (this.Vars.currentDevice != '') {
      this.Vars.body.classList.add(`on-device-${this.Vars.currentDevice}`);
    }
  }

}

export default new Capitan();